export const TIMETABLE_GROUPS = [
  { id: "tue-thu", label: "화·목", days: ["화", "목"] },
  { id: "wed-fri", label: "수·금", days: ["수", "금"] },
  { id: "sat", label: "토", days: ["토"] },
  { id: "sun", label: "일", days: ["일"] },
];
export function timeMinutes(value) {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value || "");
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}
export function buildAcademyTimetable(courses, teachers = []) {
  const blocks = [], unplaced = [], columns = new Map();
  for (const course of courses) {
    const teacher = teachers.find((t) => t.id === course.teacherId);
    const teacherKey = course.teacherId ||
      (course.assignmentVisible === false ? "masked" : "unassigned");
    if (!columns.has(teacherKey)) {
      columns.set(teacherKey, {
        id: teacherKey,
        name: teacher?.displayName || teacher?.name ||
          (course.assignmentVisible === false ? "담당 미표시" : "담당 미지정"),
      });
    }
    for (const kind of ["class", "clinic"]) {
      const days = [
        ...new Set(
          kind === "class"
            ? (course.days?.length
              ? course.days
              : course.day
              ? [course.day]
              : [])
            : course.clinicDays || [],
        ),
      ];
      const startsAt = kind === "class"
        ? course.startTime
        : course.clinicStartTime;
      const endsAt = kind === "class" ? course.endTime : course.clinicEndTime;
      if (kind === "clinic" && !days.length && !startsAt && !endsAt) continue;
      const start = timeMinutes(startsAt), end = timeMinutes(endsAt);
      if (!days.length || start === null || end === null || end <= start) {
        unplaced.push({
          course,
          kind,
          days,
          startsAt,
          endsAt,
          reason: !days.length ? "요일 미지정" : "시간 확인 필요",
        });
        continue;
      }
      const remaining = new Set(days);
      for (const group of TIMETABLE_GROUPS) {
        if (kind === "clinic" && group.days.length > 1) continue;
        if (!group.days.every((day) => remaining.has(day))) continue;
        group.days.forEach((day) => remaining.delete(day));
        blocks.push({
          id: `${course.id}-${kind}-${group.id}`,
          course,
          kind,
          teacherKey,
          groupId: group.id,
          start,
          end,
          days: group.days,
          startsAt,
          endsAt,
        });
      }
      if (remaining.size) {
        unplaced.push({
          course,
          kind,
          days: [...remaining],
          startsAt,
          endsAt,
          reason: kind === "clinic" ? "평일 클리닉" : "요일 묶음 확인 필요",
        });
      }
    }
  }
  const start = Math.min(
    9 * 60,
    ...blocks.map((b) => Math.floor(b.start / 60) * 60),
  );
  const end = Math.max(
    22 * 60,
    ...blocks.map((b) => Math.ceil(b.end / 60) * 60),
  );
  return {
    blocks,
    unplaced,
    start,
    end,
    teachers: [...columns.values()].sort((a, b) =>
      a.name.localeCompare(b.name, "ko") || a.id.localeCompare(b.id)
    ),
  };
}
// Use a common number of lanes within one teacher/day group so collisions never cover each other.
export function timetableLanes(blocks) {
  const ends = [];
  const placed = [...blocks].sort((a, b) =>
    a.start - b.start || a.end - b.end || a.id.localeCompare(b.id)
  ).map((block) => {
    let lane = ends.findIndex((end) => end <= block.start);
    if (lane < 0) lane = ends.length;
    ends[lane] = block.end;
    return { ...block, lane };
  });
  return placed.map((block) => ({ ...block, laneCount: ends.length || 1 }));
}

// Cards use the actual saved weekdays, including legacy schedules outside paired columns.
export function todayTimetable(courses, now = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now).filter((p) => p.type !== "literal").map(
      (p) => [p.type, p.value],
    ),
  );
  const day = {
    Sun: "일",
    Mon: "월",
    Tue: "화",
    Wed: "수",
    Thu: "목",
    Fri: "금",
    Sat: "토",
  }[parts.weekday];
  const minute = Number(parts.hour) * 60 + Number(parts.minute);
  const items = [];
  for (const course of courses) {
    if (
      course.active === false ||
      (course.year && Number(course.year) !== Number(parts.year))
    ) continue;
    for (const kind of ["class", "clinic"]) {
      const days = kind === "class"
        ? course.days?.length ? course.days : [course.day]
        : course.clinicDays || [];
      const startsAt = kind === "class"
        ? course.startTime
        : course.clinicStartTime;
      const endsAt = kind === "class" ? course.endTime : course.clinicEndTime;
      const start = timeMinutes(startsAt), end = timeMinutes(endsAt);
      if (
        !days.includes(day) || start === null || end === null || start >= end
      ) continue;
      items.push({
        course,
        kind,
        startsAt,
        endsAt,
        start,
        end,
        id: `${course.id}-${kind}`,
      });
    }
  }
  items.sort((a, b) =>
    a.start - b.start || a.end - b.end || a.id.localeCompare(b.id)
  );
  const current = items.filter((item) =>
    item.start <= minute && minute < item.end
  );
  const upcoming = items.filter((item) => item.start > minute);
  const next = upcoming.filter((item) => item.start === upcoming[0]?.start);
  return {
    current,
    next,
    label: `${Number(parts.month)}/${
      Number(parts.day)
    }(${day}) ${parts.hour}:${parts.minute}`,
  };
}
